import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/authStore';
import { authApi } from '@/services/api';
import { isDebugMode } from '@/config/features';
import { debug } from '@/utils/debug';
import { hasStoredAuthTokens } from '@/utils/session';
import { Logo } from '@/components/Logo';
import { EmailNotVerifiedModal } from '@/components/EmailNotVerifiedModal';
import { LoadingSpinner } from '@/components/ui';

// Form validation schema
const loginSchema = z.object({
  username: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type LoginForm = z.infer<typeof loginSchema>;

// Google Identity Services types - shared with GoogleSignInButton component
interface GoogleIdConfig {
  client_id: string;
  callback: (response: { credential: string }) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
  ux_mode?: 'popup' | 'redirect';
}

interface GoogleButtonOptions {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  width?: number;
  logo_alignment?: 'left' | 'center';
  click_listener?: () => void;
}

interface GooglePromptNotification {
  isNotDisplayed: () => boolean;
  isSkippedMoment: () => boolean;
  isDismissedMoment: () => boolean;
  getNotDisplayedReason: () => string;
  getSkippedReason: () => string;
  getDismissedReason: () => string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleIdConfig) => void;
          prompt: (momentListener?: (notification: GooglePromptNotification) => void) => void;
          renderButton: (parent: HTMLElement, options: GoogleButtonOptions) => void;
        };
      };
    };
  }
}

export function Login() {
  const { isAuthenticated, login, isLoading, checkAuth, clearError } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const setAuthFromGoogle = useAuthStore((state) => state.setAuthFromGoogle);
  const [debugMode, setDebugMode] = useState(isDebugMode());
  const [showEmailNotVerifiedModal, setShowEmailNotVerifiedModal] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<{ email: string; memberUuid: string } | null>(null);

  // Track if we're verifying existing tokens (for "open in new tab" scenario)
  const [isVerifyingTokens, setIsVerifyingTokens] = useState(() => hasStoredAuthTokens());

  // If tokens exist in localStorage, verify them before showing login form
  useEffect(() => {
    if (hasStoredAuthTokens() && !isAuthenticated && !isLoading) {
      debug.log('Login: Found stored tokens, verifying...');
      checkAuth().finally(() => {
        setIsVerifyingTokens(false);
      });
    } else if (!hasStoredAuthTokens()) {
      setIsVerifyingTokens(false);
    }
  }, [isAuthenticated, isLoading, checkAuth]);

  // Google sign-in state
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isGoogleReady, setIsGoogleReady] = useState(false);
  const [showGoogleNotLinked, setShowGoogleNotLinked] = useState(false);
  const [googleEmail, setGoogleEmail] = useState<string | null>(null);
  const googleButtonRef = React.useRef<HTMLDivElement>(null);
  const googleInitializedRef = React.useRef(false);
  const googleSignInMutateRef = React.useRef<((idToken: string) => void) | null>(null);

  const clientId = import.meta.env.VITE_GOOGLE_SIGNIN_CLIENT_ID;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  // Google sign-in mutation
  const googleSignInMutation = useMutation({
    mutationFn: (idToken: string) =>
      authApi.googleSignIn({
        id_token: idToken,
        signup_intent: false,
      }),
    onSuccess: (response) => {
      setIsGoogleLoading(false);
      if ('requires_company_info' in response && response.requires_company_info) {
        // New user - redirect to signup to complete
        debug.log('Login: Google user needs to complete signup', response);
        sessionStorage.setItem('googleSignupPending', JSON.stringify(response));
        navigate('/signup?google=pending');
      } else if ('access_token' in response) {
        // Existing user - complete sign-in
        debug.log('Login: Google Sign-In successful');
        setAuthFromGoogle(response.access_token, response.member_uuid);
        toast.success('Signed in with Google successfully!');
        const locationState = location.state as { from?: { pathname?: string } } | null;
        const from = locationState?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      }
    },
    onError: (error: unknown) => {
      setIsGoogleLoading(false);
      const apiError = error as {
        response?: {
          status?: number;
          data?: {
            detail?: string | { code?: string; message?: string; email?: string };
          };
        };
      };

      // Check if this is the "email exists but not linked" error
      if (apiError.response?.status === 409) {
        const detail = apiError.response.data?.detail;
        if (typeof detail === 'object' && detail?.code === 'email_exists_no_google_link') {
          // Show the "not linked" message with option to connect
          setGoogleEmail(detail.email || null);
          setShowGoogleNotLinked(true);
          return;
        }
      }

      // Generic error
      const errorMessage = typeof apiError.response?.data?.detail === 'string'
        ? apiError.response.data.detail
        : 'Failed to sign in with Google';
      toast.error(errorMessage);
    },
  });

  // Keep mutation ref updated
  useEffect(() => {
    googleSignInMutateRef.current = googleSignInMutation.mutate;
  }, [googleSignInMutation.mutate]);

  // Listen for debug mode changes
  useEffect(() => {
    const unsubscribe = debug.onModeChange(() => {
      setDebugMode(isDebugMode());
    });
    return unsubscribe;
  }, []);

  // Pre-fill test credentials in development
  useEffect(() => {
    if (debugMode) {
      setValue('username', 'admin@example.com');
      setValue('password', 'secret');
      debug.log('Login: Pre-filled test credentials for development');
    } else {
      setValue('username', '');
      setValue('password', '');
    }
  }, [debugMode, setValue]);

  // Clear errors when component mounts
  useEffect(() => {
    clearError();
    setShowGoogleNotLinked(false);
    setGoogleEmail(null);
  }, [clearError]);

  // Initialize Google button after ref is available
  const initializeGoogleButton = React.useCallback(() => {
    if (!window.google?.accounts?.id || !clientId || !googleButtonRef.current) {
      return;
    }

    // Prevent multiple initializations
    if (googleInitializedRef.current) {
      return;
    }
    googleInitializedRef.current = true;

    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => {
          // Keep loading state true - it will be set to false in mutation callbacks
          if (response.credential) {
            googleSignInMutateRef.current?.(response.credential);
          } else {
            setIsGoogleLoading(false);
          }
        },
        auto_select: false,
        ux_mode: 'popup',
      });

      // Render Google's button in a hidden container
      // When clicked, it opens the proper account chooser
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        width: 280,
        click_listener: () => {
          setIsGoogleLoading(true);
          setShowGoogleNotLinked(false);
        },
      });

      setIsGoogleReady(true);
      debug.log('Login: Google Sign-In button initialized');
    } catch (error) {
      debug.error('Login: Failed to initialize Google Sign-In', error);
    }
  }, [clientId]);

  // Load Google Identity Services and render the hidden button
  useEffect(() => {
    if (!clientId) {
      debug.log('Login: Google Sign-In client ID not configured');
      return;
    }

    // Check if script is already loaded
    if (window.google?.accounts?.id) {
      initializeGoogleButton();
      return;
    }

    // Load the Google Identity Services script
    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existingScript) {
      // Script exists, wait for it to load
      existingScript.addEventListener('load', initializeGoogleButton);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogleButton;
    script.onerror = () => {
      debug.error('Login: Failed to load Google Identity Services');
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup not needed - script should persist
    };
  }, [clientId, initializeGoogleButton]);

  // Reset loading state when window regains focus (popup closed)
  useEffect(() => {
    const handleFocus = () => {
      // Small delay to allow Google's callback to fire first
      setTimeout(() => {
        if (!googleSignInMutation.isPending) {
          setIsGoogleLoading(false);
        }
      }, 500);
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [googleSignInMutation.isPending]);

  // Handle our custom button click - trigger the hidden Google button
  const handleGoogleSignIn = () => {
    if (!isGoogleReady || !googleButtonRef.current) {
      if (!clientId) {
        toast.error('Google sign-in is unavailable. Please create a new account or sign in with email.');
      } else {
        toast.error('Google sign-in is loading. Please try again.');
      }
      return;
    }

    // Find and click the actual Google button inside the container
    const googleButton = googleButtonRef.current.querySelector('div[role="button"]') as HTMLElement;
    if (googleButton) {
      setIsGoogleLoading(true);
      setShowGoogleNotLinked(false);
      googleButton.click();
    } else {
      toast.error('Unable to start Google sign-in. Please try again or sign in with email.');
    }
  };

  // Redirect if already authenticated
  if (isAuthenticated) {
    const locationState = location.state as { from?: { pathname?: string } } | null;
    const from = locationState?.from?.pathname || '/dashboard';
    debug.log('Login: Already authenticated, redirecting to', from);
    return <Navigate to={from} replace />;
  }

  // Show loading while verifying existing tokens (e.g., "open in new tab" scenario)
  // Include header placeholder so spinner doesn't shift when real header appears
  if (isVerifyingTokens || isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        {/* Header placeholder — must mirror the real desktop <Header /> wrapper
            exactly (`pl-5 pr-4 sm:pr-6 lg:pr-8`, no max-w-7xl, no border, with
            `translate-x-0.5` on the icon). Otherwise the flower visibly jumps
            left when the placeholder unmounts and the real header takes over. */}
        <div className="h-[var(--header-h)] bg-flouri-darkGray flex-shrink-0">
          <div className="h-full pl-5 pr-4 sm:pr-6 lg:pr-8 flex items-center">
            <svg viewBox="0 0 700 700" className="w-6 h-6 translate-x-0.5" style={{ overflow: 'visible' }}>
              <path fill="#fff" d="M251.9,383.35v-92.47c0-59.4-48.15-107.55-107.55-107.55h0c-59.4,0-107.55,48.15-107.55,107.55h0c0,59.4,48.15,107.55,107.55,107.55h92.47c8.33,0,15.08-6.75,15.08-15.08Z" />
              <path fill="#fff" d="M236.82,449.42h-92.47c-59.4,0-107.55,48.15-107.55,107.55h0c0,59.4,48.15,107.55,107.55,107.55h0c59.4,0,107.55-48.15,107.55-107.55v-92.47c0-8.33-6.75-15.08-15.08-15.08Z" />
              <path fill="#fff" d="M488.92,26.88h-.31c-102.67,0-185.9,83.23-185.9,185.9v169.54c0,8.9,7.22,16.12,16.12,16.12h170.09c102.36,0,185.34-82.98,185.34-185.34v-.87c0-102.36-82.98-185.34-185.34-185.34Z" />
              <path fill="#373636" d="M526.96,261.37c0,45.74-37.08,82.82-82.82,82.82h-71.99c-6.12,0-11.07-4.96-11.07-11.07v-71.74c0-45.88,37.19-83.06,83.06-83.06h0c45.74,0,82.82,37.08,82.82,82.82v.25Z" />
              <path fill="#fff" d="M302.71,465v91.98c0,59.4,48.15,107.55,107.55,107.55h0c59.4,0,107.55-48.15,107.55-107.55h0c0-59.4-48.15-107.55-107.55-107.55h-91.98c-8.6,0-15.57,6.97-15.57,15.57Z" />
            </svg>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" variant="primary" className="mx-auto mb-4" label="Checking authentication" />
            <p className="text-gray-600">Checking authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: LoginForm) => {
    debug.log('Login: Submitting form for', data.username);
    try {
      await login(data);
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { detail?: { email_verification_required?: boolean; email?: string; member_uuid?: string } } } };
      if (err?.response?.status === 403 && err?.response?.data?.detail?.email_verification_required) {
        const detail = err.response.data.detail;
        debug.log('Login: Email not verified', detail);
        setUnverifiedEmail({
          email: detail.email || data.username || '',
          memberUuid: detail.member_uuid || '',
        });
        setShowEmailNotVerifiedModal(true);
        clearError();
      } else {
        debug.error('Login: Form submission failed', err);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full space-y-8" style={{ maxWidth: '384px', margin: '0 auto' }}>
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <Logo variant="color" size="lg" className="!h-[64px] ml-6" />
          </div>
          <p className="text-gray-600">Let's get to flourishing.</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-2 bg-white shadow-lg rounded-lg px-8 py-10" style={{ width: '100%', maxWidth: '384px', boxSizing: 'border-box' }}>
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700">
              Email Address
            </label>
            <input
              {...register('username')}
              type="email"
              id="username"
              className={`mt-1 appearance-none relative block w-full px-3 py-2 border rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm ${
                errors.username ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter registered email address"
              autoComplete="email"
            />
            <div className="h-5 mt-1">
              {errors.username && (
                <p className="text-sm text-red-600">{errors.username.message}</p>
              )}
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <a
                href="/forgot-password"
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Forgot password?
              </a>
            </div>
            <input
              {...register('password')}
              type="password"
              id="password"
              className={`mt-1 appearance-none relative block w-full px-3 py-2 border rounded-md placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm ${
                errors.password ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter your password"
              autoComplete="current-password"
            />
            <div className="h-5 mt-1">
              {errors.password && (
                <p className="text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors ${
              isSubmitting || isLoading
                ? 'cursor-not-allowed'
                : 'hover:opacity-90'
            }`}
            style={{
              background: isSubmitting || isLoading
                ? '#fca5a5'
                : 'linear-gradient(90deg, #ff3160 0%, #cc1a40 50%, #ff3160 100%)',
            }}
          >
            {isSubmitting || isLoading ? (
              <>
                <LoadingSpinner size="md" variant="white" className="-ml-1 mr-3" label="Logging in" />
                Logging in...
              </>
            ) : (
              'Log in'
            )}
          </button>

          {/* Divider */}
          <div className="relative my-4 pt-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or</span>
            </div>
          </div>

          {/* Hidden container for Google's rendered button */}
          <div
            ref={googleButtonRef}
            style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }}
            aria-hidden="true"
          />

          {/* Our custom Google Sign-In Button */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isGoogleLoading || isSubmitting || isLoading}
            className={`w-full flex items-center justify-center gap-3 py-2 px-4 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
              isGoogleLoading ? 'cursor-not-allowed opacity-70' : ''
            }`}
          >
            {isGoogleLoading ? (
              <LoadingSpinner size="sm" variant="primary" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            {isGoogleLoading ? 'Logging in...' : 'Log in with Gmail'}
          </button>

          {/* Google Not Linked Message */}
          {showGoogleNotLinked && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-800 mb-3">
                {googleEmail ? (
                  <>An account exists for <strong>{googleEmail}</strong>, but Gmail sign-in is not connected.</>
                ) : (
                  <>An account exists with this email, but Gmail sign-in is not connected.</>
                )}
              </p>
              <p className="text-sm text-blue-700 mb-3">
                Sign in with your password, then connect Gmail in Settings to use it for future sign-ins.
              </p>
              <button
                type="button"
                onClick={() => setShowGoogleNotLinked(false)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Got it
              </button>
            </div>
          )}

          {/* Signup Link */}
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <a href="/signup" className="font-medium text-primary-600 hover:text-primary-700">
                Sign up for free
              </a>
            </p>
          </div>
        </form>

        {debugMode && (
          <div className="mt-4 p-4 bg-gray-100 rounded-lg text-center">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Development Mode</h3>
            <p className="text-xs text-gray-600">Test credentials have been pre-filled.</p>
            <p className="text-xs text-gray-600 mt-1">API URL: {import.meta.env.VITE_API_URL}</p>
          </div>
        )}
      </div>

      {/* Email Not Verified Modal */}
      {unverifiedEmail && (
        <EmailNotVerifiedModal
          isOpen={showEmailNotVerifiedModal}
          onClose={() => setShowEmailNotVerifiedModal(false)}
          email={unverifiedEmail.email}
          memberUuid={unverifiedEmail.memberUuid}
        />
      )}
    </div>
  );
}
